import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import defaultProductImg from "../../../img/default-image_420.png";
import type { CartItem } from "../../models/cart/types";
import { statusLabel, statusColor } from "../../utils/inventoryStatus";
import CartSpecificationLines, {
  type BoxSelectorData,
} from "./cartSpecificationLines";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";
import { isSAPSource } from "@/app/utils/erpHelper";
import { isEagleViewProduct } from "@/app/models/constants";
import { RootState } from "@/app/store/store";
import { useSelector } from "react-redux";

const formatMoney = (n?: number) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "-";

export type CartItemRowProps = {
  item: CartItem;
  onQtyChange: (q: number) => Promise<boolean>;
  onRemove: () => void;
  onOpenSidebar: (open: boolean) => void;
  onCartItemForTemplate: (item: CartItem) => void;
  loadingStatus: LoadingStatusEnum;
  cartLineRiserPan: boolean;
};

export const CartItemRow: React.FC<CartItemRowProps> = ({
  item,
  onQtyChange,
  onRemove,
  onOpenSidebar,
  onCartItemForTemplate,
  loadingStatus,
  cartLineRiserPan,
}) => {
  const [qty, setQty] = useState<number>(item.quantity);
  const isEagleView = isEagleViewProduct(item.sku);
  const invLabel = statusLabel(item.inventoryStatus);

  const productHref = `product?id=${
    item.parentProductId
      ? `${item.parentProductId}&associatedproduct=${item.productId}`
      : item.productId
  }`;

  const onChangeQtyLocal = async (next: number) => {
    if (next < 1) return;
    var oldQty = qty;
    setQty(next);
    var success = await onQtyChange(next);
    if (!success) {
      setQty(oldQty);
    }
  };

  const initialSpec: BoxSelectorData | undefined = useMemo(() => {
    if (!item.cartSpecificationLine) return undefined;
    return {
      boxType: item.box || (cartLineRiserPan ? "BOX/SQ" : ""),
      ribType: item.rib || (cartLineRiserPan ? "5-RIB" : ""),
      lines: (item.cartSpecificationLines ?? []).map((l: any) => ({
        id: l.id ?? 0,
        qty: l.qty ?? "",
        lengthFt: l.lengthFt ?? "",
        lengthIn: l.lengthIn ?? "",
      })),
    };
  }, [
    item.id,
    item.box,
    item.rib,
    item.cartSpecificationLine,
    item.cartSpecificationLines,
  ]);

  const handleBoxChange = (data: BoxSelectorData) => {
    if (!data?.lines?.length) return;

    let totalQty = 0;
    data.lines.forEach((line) => {
      const { qty, lengthFt, lengthIn } = line;

      const totalInches = Number(lengthFt) * 12 + Number(lengthIn);

      if (data.boxType === "BOX/SQ" || data.boxType === "BOX/TT") {
        const fitQty =
          (totalInches * Number(qty) + 0.178 * Number(qty) * 12) / 12;
        totalQty += Math.ceil(fitQty);
      } else {
        const fitQty = (totalInches * Number(qty)) / 12;
        totalQty += Math.ceil(fitQty);
      }
    });

    if (totalQty > 0) {
      const nextQty = Math.ceil(totalQty);
      if (nextQty == qty) return;
      setQty(nextQty);
      onQtyChange(nextQty);
    }
  };

  let warehouseSource = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.source
  );

  let isSapWarehouse = isSAPSource(warehouseSource ?? "");

  useEffect(() => {
    if (!initialSpec) return;
    handleBoxChange(initialSpec);
  }, []);

  return (
    <>
      <Box
        sx={{
          bgcolor: "transparent",
          p: 1.5,
          borderRadius: 0,
        }}
      >
        <Grid
          container
          columnSpacing={2}
          wrap="nowrap"
          sx={{ alignItems: "flex-start" }}
        >
          <Grid
            item
            xs={3}
            sm={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <a
              href={productHref}
              title={item.picture?.title ?? item.productName}
              style={{ width: "100%" }}
            >
              <Box
                component="img"
                alt={item.picture?.alternateText ?? item.productName}
                src={
                  item.picture?.imageUrl
                    ? `${process.env.REACT_APP_CDN_URL}/images/general/picture_${item.picture.imageUrl}`
                    : defaultProductImg
                }
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  backgroundColor: item.colorHex ?? "transparent",
                }}
              />
            </a>
          </Grid>

          <Grid item xs={9} sm={10}>
            <Grid container>
              <Grid item xs={12} sm={10}>
                <MuiLink
                  href={productHref}
                  underline="none"
                  color="#767676"
                  title={item.productName}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: 18,
                  }}
                >
                  <strong>
                    {(item.manufacturers?.[0] ?? "").trim()
                      ? `${item.manufacturers?.[0]} - `
                      : ""}
                  </strong>
                  {item.productName}
                </MuiLink>
                {!item.isAvailable && (
                  <Typography className="fontSize14" color="red">
                    Not available in branch
                  </Typography>
                )}
                {!!invLabel && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      color: statusColor(item.inventoryStatus),
                      fontSize: 14,
                    }}
                  >
                    {invLabel}
                  </Typography>
                )}
                {item.availabilityMessage && (
                  <Typography sx={{ color: "#000", fontSize: 14 }}>
                    {item.availabilityMessage}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatMoney(qty * item.unitPrice)}
                  </Typography>
                  {item.discount != null && item.discount !== "" && (
                    <>
                      <Typography
                        sx={{ fontSize: 14, color: "#29b05f", mt: 0.5 }}
                      >
                        You save:
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: "#29b05f" }}>
                        {item.discount}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: "30px" }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={7}>
                  <Grid container spacing={0}>
                    {!!item.uom && (
                      <Grid item xs={12}>
                        UOM:{" "}
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {item.uom}
                        </Typography>
                      </Grid>
                    )}
                    {!!item.color && (
                      <Grid item xs={12}>
                        Color:{" "}
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {item.color}
                        </Typography>
                      </Grid>
                    )}
                    {!!item.measure && (
                      <Grid item xs={12}>
                        Measure:{" "}
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {item.measure}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      Price:&nbsp;
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {`${formatMoney(item.unitPrice)} per ${
                          item.uom ?? "Unit"
                        }`}
                      </Typography>
                    </Grid>

                    {/* Mobile SKU */}
                    <Grid
                      item
                      xs={12}
                      sx={{ display: { xs: "block", md: "none" } }}
                    >
                      <Typography variant="body2">
                        SKU:&nbsp;
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {isSapWarehouse ? item.sapProduct : item.sku}
                        </Typography>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Qty controls */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ textAlign: "center", height: 54 }}>
                    <Typography
                      sx={{ textTransform: "uppercase", fontWeight: 600 }}
                    >
                      Qty
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        disabled={isEagleView}
                        onClick={() => onChangeQtyLocal(qty - 1)}
                      >
                        <RemoveIcon fontSize="small" sx={{ color: "#62B5E5" }} />
                      </IconButton>
                      <TextField
                        type="number"
                        value={qty}
                        disabled={isEagleView}
                        onChange={(e) =>
                          onChangeQtyLocal(Math.max(1, Number(e.target.value)))
                        }
                        inputProps={{ min: 1, style: { textAlign: "center" } }}
                        size="small"
                        sx={{
                          width: 60,
                          // Hide number input spinners (Chrome/Safari/Edge)
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          // Hide number input spinners (Firefox)
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input": {
                            fontFamily: "Montserrat, Verdana, sans-serif",
                            fontSize: 16,
                            border: 0,
                            py: "2px",
                            backgroundColor: "#fff",
                            textAlign: "center",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        disabled={isEagleView}
                        onClick={() => onChangeQtyLocal(qty + 1)}
                      >
                        <AddIcon fontSize="small" sx={{ color: "#62B5E5" }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Desktop SKU */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography variant="body2">
                  SKU:&nbsp;
                  <Typography component="span" sx={{ fontWeight: 700 }}>
                    {isSapWarehouse ? item.sapProduct : item.sku}
                  </Typography>
                </Typography>
              </Box>

              {/* Spec lines editor */}
              {item.cartSpecificationLine && (
                <Box sx={{ paddingTop: "10px" }}>
                  <CartSpecificationLines
                    cartItemId={item.id}
                    initialValue={initialSpec}
                    onChange={handleBoxChange}
                    cartLineRiserPan={cartLineRiserPan}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: "rgba(0,0,0,0.08)" }} />
        <Box
          sx={{
            color: "#003B5C",
            fontWeight: 600,
            fontFamily: "Montserrat",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {!isEagleView && (
            <>
              <MuiLink
                component="button"
                type="button"
                sx={{ color: "#003B5C", textDecoration: "none" }}
                onClick={() => {
                  onCartItemForTemplate(item);
                  onOpenSidebar(true);
                }}
              >
                Save to Template or Estimate
              </MuiLink>
              <Typography component="span" sx={{ mx: 0.5 }}>
                |
              </Typography>
            </>
          )}
          <MuiLink
            component="button"
            type="button"
            disabled={loadingStatus === LoadingStatusEnum.LOADING}
            sx={{
              color:
                loadingStatus === LoadingStatusEnum.LOADING
                  ? "#707372"
                  : "#003B5C",
              cursor:
                loadingStatus === LoadingStatusEnum.LOADING
                  ? "default"
                  : "pointer",
              textDecoration: "none",
            }}
            onClick={onRemove}
          >
            Remove
          </MuiLink>
        </Box>
      </Box>
      <Box sx={{ py: 2, bgcolor: "#f6f6f6" }}></Box>
    </>
  );
};

export default CartItemRow;
